import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { rentalService } from "@/features/rentals/services/rental.service";
import { useRentalSummary } from "@/features/rentals/hooks/useRentalSummary";
import { useRentalList } from "@/features/rentals/hooks/useRentalList";
import type { RentalListFilters } from "@/features/rentals/hooks/useRentalList";
import { RentalSummaryCards } from "@/features/rentals/components/RentalSummaryCards";
import { RentalFilters } from "@/features/rentals/components/RentalFilters";
import { RentalsTable } from "@/features/rentals/components/RentalsTable";
import { RentalDetailsModal } from "@/features/rentals/components/RentalDetailsModal";
import { RentalEditDialog } from "@/features/rentals/components/RentalEditDialog";
import { RentalCancelDialog } from "@/features/rentals/components/RentalCancelDialog";
import type {
  RentalResponseDTO,
  RentalSummaryParams,
  RentalUpdateRequestDTO,
} from "@/features/rentals/types/rental.types";

const DIALOG_MODE = {
  DETAILS: "details",
  EDIT: "edit",
  CANCEL: "cancel",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

function getCurrentMonthRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const startDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
  const endDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return { startDate, endDate };
}

function buildSummaryParams(filters: RentalListFilters): RentalSummaryParams {
  // Espelha a listagem: busca por ID ignora os demais filtros e busca por
  // contrato ignora o período, para os cards refletirem o que a lista mostra.
  if (filters.rentalId) {
    return { rentalId: filters.rentalId };
  }
  if (filters.contractNumbers?.length) {
    return {
      contractNumbers: filters.contractNumbers,
      rentalTypes: filters.rentalTypes,
      statuses: filters.statuses,
      sellerIds: filters.sellerIds,
      clientName: filters.clientName,
    };
  }
  return {
    startDate: filters.startDate,
    endDate: filters.endDate,
    rentalTypes: filters.rentalTypes,
    statuses: filters.statuses,
    contractNumbers: filters.contractNumbers,
    sellerIds: filters.sellerIds,
    clientName: filters.clientName,
  };
}

export function VerAlugueisPage() {
  const [searchParams] = useSearchParams();
  // Deep link `?rentalId=X` (vindo do modal de cliente, por exemplo): lido
  // uma única vez na montagem — depois o estado dos filtros é soberano.
  const [deepLinkRentalId] = useState<number | undefined>(() => {
    const raw = searchParams.get("rentalId");
    if (!raw || !/^\d+$/.test(raw)) return undefined;
    const parsed = Number(raw);
    return parsed >= 1 && parsed <= 2147483647 ? parsed : undefined;
  });

  const initialPeriod = useMemo(() => getCurrentMonthRange(), []);
  const initialFilters = useMemo<RentalListFilters>(
    () => ({
      startDate: initialPeriod.startDate,
      endDate: initialPeriod.endDate,
      // Modo busca-por-ID: o hook omite os demais filtros na request.
      rentalId: deepLinkRentalId,
    }),
    [initialPeriod, deepLinkRentalId],
  );

  const list = useRentalList({ initialFilters });
  const summary = useRentalSummary({
    initialParams: buildSummaryParams(initialFilters),
  });

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selected, setSelected] = useState<RentalResponseDTO | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const openDialog = useCallback(
    (mode: DialogMode, rental: RentalResponseDTO) => {
      setSelected(rental);
      setDialogMode(mode);
      setDialogContent(mode);
    },
    [],
  );

  const closeDialog = useCallback(() => {
    setDialogMode(null);
  }, []);

  // Deep link: abre o detalhe do aluguel quando ele chega na lista.
  // Uma única vez por montagem (ref) — fechar o modal não o reabre.
  // Adiado para o próximo tick: evita setState síncrono no efeito.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!deepLinkRentalId || autoOpenedRef.current) return;
    const rental = list.data.find((item) => item.id === deepLinkRentalId);
    if (!rental) return;
    const timer = setTimeout(() => {
      autoOpenedRef.current = true;
      openDialog(DIALOG_MODE.DETAILS, rental);
    }, 0);
    return () => clearTimeout(timer);
  }, [deepLinkRentalId, list.data, openDialog]);

  const handleApplyFilters = useCallback(
    (filters: RentalListFilters) => {
      list.setFilters(filters);
      summary.setParams(buildSummaryParams(filters));
    },
    [list, summary],
  );

  const handleClearFilters = useCallback(() => {
    const cleared: RentalListFilters = {
      startDate: initialPeriod.startDate,
      endDate: initialPeriod.endDate,
    };
    list.setFilters(cleared);
    summary.setParams(buildSummaryParams(cleared));
  }, [list, summary, initialPeriod]);

  const handleReload = useCallback(() => {
    list.reload();
    summary.reload();
  }, [list, summary]);

  const handleEditSubmit = async (data: RentalUpdateRequestDTO) => {
    if (!selected) return;
    setDialogLoading(true);
    try {
      const updated = await rentalService.update(selected.id, data);
      list.updateLocal(updated);
      summary.reload();
      toast.success("Aluguel atualizado");
      closeDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao atualizar aluguel"));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!selected) return;
    setDialogLoading(true);
    try {
      const updated = await rentalService.cancel(selected.id);
      list.updateLocal(updated);
      summary.reload();
      toast.success("Aluguel cancelado");
      closeDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao cancelar aluguel"));
    } finally {
      setDialogLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Aluguéis</h1>
          <p className="text-muted-foreground">
            Lista completa de aluguéis da loja com filtros e ações.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReload}
          disabled={list.loading}
          className="gap-2 self-start md:self-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      <RentalSummaryCards
        summary={summary.data}
        loading={summary.loading}
        revenueLabel={
          // Segue os filtros APLICADOS (não o draft): nos modos ID/contrato
          // o summary é buscado sem período, e "do período" mentiria.
          list.filters.rentalId
            ? "Receita do aluguel"
            : list.filters.contractNumbers?.length
              ? "Receita do contrato"
              : undefined
        }
      />

      <Card>
        <CardContent className="space-y-6">
          <RentalFilters
            filters={list.filters}
            loading={list.loading}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
          />

          {list.loading && list.data.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <Spinner className="w-6 h-6" />
            </div>
          ) : list.error && list.data.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-destructive">{list.error}</p>
              <Button variant="outline" size="sm" onClick={list.reload}>
                Tentar novamente
              </Button>
            </div>
          ) : list.data.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              Nenhum aluguel encontrado para os filtros aplicados.
            </div>
          ) : (
            <RentalsTable
              rentals={list.data}
              loading={list.loading}
              currentPage={list.page}
              totalPages={list.totalPages}
              onView={(rental) => openDialog(DIALOG_MODE.DETAILS, rental)}
              onEdit={(rental) => openDialog(DIALOG_MODE.EDIT, rental)}
              onCancel={(rental) => openDialog(DIALOG_MODE.CANCEL, rental)}
              onPageChange={list.setPage}
            />
          )}
        </CardContent>
      </Card>

      {dialogContent === DIALOG_MODE.DETAILS && (
        <RentalDetailsModal
          open={dialogMode === DIALOG_MODE.DETAILS}
          rental={selected}
          onClose={closeDialog}
        />
      )}
      {dialogContent === DIALOG_MODE.EDIT && (
        <RentalEditDialog
          open={dialogMode === DIALOG_MODE.EDIT}
          loading={dialogLoading}
          rental={selected}
          onClose={closeDialog}
          onSubmit={handleEditSubmit}
        />
      )}
      {dialogContent === DIALOG_MODE.CANCEL && (
        <RentalCancelDialog
          open={dialogMode === DIALOG_MODE.CANCEL}
          loading={dialogLoading}
          rental={selected}
          onClose={closeDialog}
          onConfirm={handleCancelConfirm}
        />
      )}
    </div>
  );
}
